import React, { useState, useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tooltip, Tag } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateControlPanelDrawerPlacement,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import MonitoringIconsMovingWalks from "../monitoringIcons/MonitoringIconsMovingWalks";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { BankInterface } from "../../../interfaces/Bank.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
// UTILS
import { emptyFunctionDeclaration } from "../../../utils/utils";
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { useCanOpenControlPanel } from "../../../customHooks/useScopedPermission";
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
// UUID
import { stringToUuidT } from "../../../utils/uuid";

function MonitoringLiftMovingWalksFullView({
  lift,
  bank,
  serverId,
  channelId,
}: {
  bank: BankInterface;
  lift: LiftInterface;
  serverId: string;
  channelId: string;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [liftId, setLiftId] = useState<string>("");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode,
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(liftId), [liftId]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = previewFaultServiceMode || socketMessageLift.faults;

  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessageLift.serviceMode?.messageType ?? null,
        socketMessageLift.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessageLift.serviceMode],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getDrawerPlacement = (e: any) => {
    const clickX = e.clientX;
    const screenWidth = window.innerWidth;
    if (clickX > screenWidth / 2) {
      dispatch(updateControlPanelDrawerPlacement("left"));
    } else {
      dispatch(updateControlPanelDrawerPlacement("right"));
    }
  };

  // The lift name opens the panel only when at least one of the panel's tabs
  // would render for this role (see CONTROL_PANEL_TAB_GATES); MASTER ADMIN always.
  // Deliberately NOT tied to the Banks switch: the Roles editor's Monitoring
  // Dashboard Banks block shows the lifts, it does not make their names clickable.
  const canOpenLiftControlPanel = useCanOpenControlPanel("lift");

  const onClickOpenModalElevator = (e: any) => {
    // With no tab to show, the lift name is inert; the per-tab permissions only
    // matter once the panel IS open.
    if (!canOpenLiftControlPanel) return;
    getDrawerPlacement(e);
    dispatch(
      updateModalControlPanelTab({
        liftType: "MOVINGWALKS",
        tab: hasPermission(Permission.MonitorControlPanelActions, permissionSet ?? [])
          ? "ACTIONS"
          : "FAULTS",
      }),
    );
    dispatch(updateLiftSelected(lift));
    dispatch(updateBankSelected(bank));
    dispatch(updateChannelId(channelId));
    dispatch(updateServerId(serverId));
    dispatch(updateOpenModalControlPanelLift(true));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (lift && lift.id && String(lift.id) !== "") {
      setLiftId(String(lift.id));
    }
  }, [lift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {/* Top container */}
      <div
        className={`monitoringView__topViewContainer__${theme} ${
          hasFaults
            ? "monitoringView__dangerElevator monitoringView__dangerElevator__top monitoringView__dangerElevator__bottom"
            : ""
        }`}
        style={{
          height: "50px",
          width: "45px",
          margin: "5px",
          marginTop: "1px",
          textAlign: "center",
          paddingTop: "5px",
        }}
      >
        <MonitoringIconsMovingWalks
          direction={socketMessageLift.direction}
          iconSize={40}
          mapViewClass=""
        />
      </div>
      {/* Bottom container */}
      <div
        role="button"
        tabIndex={0}
        onKeyDown={emptyFunctionDeclaration}
        onClick={onClickOpenModalElevator}
        style={{
          fontSize: "9px",
          fontWeight: "bolder",
          textAlign: "center",
        }}
      >
        {/* Lift name */}
        <div>
          <Tooltip placement="top" title={String(lift.name ?? "")}>
            <span className={hasFaults ? "generalStyles__error" : ""}>
              {String(lift.name ?? "")
                .toUpperCase()
                .substring(0, 6)}
            </span>
          </Tooltip>
        </div>
        {/* Fault tag */}
        {hasFaults && (
          <div className="generalStyles__flexCenter" style={{ textAlign: "center" }}>
            <Tooltip placement="top" title="Fault">
              <Tag
                style={{
                  marginTop: "2px",
                  marginLeft: "3px",
                  fontSize: "9px",
                  width: "43px",
                }}
                color="red"
              >
                FAULT
              </Tag>
            </Tooltip>
          </div>
        )}
        {/* Service mode tag */}
        {(serviceModeInfo || previewFaultServiceMode) && (
          <div>
            <Tooltip
              placement="top"
              title={serviceModeInfo ? serviceModeInfo.displayName : "Energy Saving"}
            >
              <Tag
                style={{
                  marginLeft: "3px",
                  marginTop: "5px",
                  width: "43px",
                }}
                color={serviceModeInfo ? serviceModeInfo.color || "cyan" : "blue"}
              >
                {serviceModeInfo ? serviceModeInfo.abbreviation.substring(0, 3) : "ENER"}
              </Tag>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

export default MonitoringLiftMovingWalksFullView;
